import { useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for haptic feedback on mobile devices
 * Uses the Vibration API with fallbacks for unsupported devices
 */

const useHaptic = () => {
  const isSupported = useRef(false);
  const lastVibration = useRef(0);
  const minInterval = 50; // Minimum time between vibrations (ms)

  useEffect(() => {
    // Check if Vibration API is supported
    isSupported.current = 'vibrate' in navigator;
    
    // Log support status in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Haptic feedback supported:', isSupported.current);
    }
  }, []);

  /**
   * Trigger vibration with throttling to prevent over-vibration
   */
  const vibrate = useCallback((pattern) => {
    if (!isSupported.current) return;

    const now = Date.now();
    if (now - lastVibration.current < minInterval) return;

    try {
      navigator.vibrate(pattern);
      lastVibration.current = now;
    } catch (error) {
      console.error('Vibration error:', error);
    }
  }, []);

  /**
   * Light tap feedback (10ms)
   * Use for: hover effects, focus, minor interactions
   */
  const light = useCallback(() => {
    vibrate(10);
  }, [vibrate]);

  /**
   * Medium tap feedback (20ms)
   * Use for: button clicks, selections, confirmations
   */
  const medium = useCallback(() => {
    vibrate(20);
  }, [vibrate]);

  /**
   * Heavy tap feedback (40ms)
   * Use for: important actions, deletions, warnings
   */
  const heavy = useCallback(() => {
    vibrate(40);
  }, [vibrate]);

  /**
   * Success pattern (20ms, pause, 20ms)
   * Use for: form submissions, successful operations
   */
  const success = useCallback(() => {
    vibrate([20, 50, 20]);
  }, [vibrate]);

  /**
   * Error pattern (50ms, pause, 50ms, pause, 50ms)
   * Use for: errors, validation failures
   */
  const error = useCallback(() => {
    vibrate([50, 100, 50, 100, 50]);
  }, [vibrate]);

  /**
   * Warning pattern (30ms, pause, 30ms)
   * Use for: warnings, confirmations needed
   */
  const warning = useCallback(() => {
    vibrate([30, 100, 30]);
  }, [vibrate]);

  /**
   * Selection pattern (15ms)
   * Use for: selecting items, toggling switches
   */
  const selection = useCallback(() => {
    vibrate(15);
  }, [vibrate]);

  /**
   * Notification pattern (20ms, pause, 10ms)
   * Use for: notifications, alerts
   */
  const notification = useCallback(() => {
    vibrate([20, 100, 10]);
  }, [vibrate]);

  /**
   * Long press pattern (100ms)
   * Use for: long press actions, context menus
   */
  const longPress = useCallback(() => {
    vibrate(100);
  }, [vibrate]);

  /**
   * Cancel ongoing vibration
   */
  const cancel = useCallback(() => {
    if (isSupported.current) {
      try {
        navigator.vibrate(0);
      } catch (error) {
        console.error('Cancel vibration error:', error);
      }
    }
  }, []);

  /**
   * Custom vibration pattern
   * @param {number|number[]} pattern - Vibration pattern
   */
  const custom = useCallback((pattern) => {
    vibrate(pattern);
  }, [vibrate]);

  return {
    isSupported: isSupported.current,
    light,
    medium,
    heavy,
    success,
    error,
    warning,
    selection,
    notification,
    longPress,
    cancel,
    custom,
  };
};

export default useHaptic;

